import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import HScrollRow from './HScrollRow';
import { useNavigate, useLocation } from 'react-router-dom';
import { getHeaders } from '../lib/useQobuz';
import { CLOUD_API_URL } from '../config/serverConfig';
import { normalizeAlbum } from '../utils/albumUtils';
import { getQualityTier } from '../hooks/useAudioQuality';
import { getAuthState } from './AuthView';
import { getCoverArtWithFallback, isCoverIPCAvailable, normalizeCoverHash } from '../lib/coverArtIPC';
import { isBadCoverUrl, isCoverProxyUrl } from '../utils/coverArt';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { maestroRadioService } from '../services/maestroRadioService';
import { sharingService } from '../services/sharingService';
import { prefersFinePointerHover } from '../utils/touchUi';
import AlbumCoverCard, { CARD_SIZE } from './AlbumCoverCard';
import useGridSections from '../hooks/useGridSections';
import useScrollRestore from '../hooks/useScrollRestore';
import useHomeSectionOrder from '../hooks/useHomeSectionOrder';
import { buildHomeSections } from './home/buildHomeSections';
import HomeSectionList from './home/HomeSectionList';
import { showImperativeToast } from '../providers/UIProvider';
import { filterRenderableRecentAlbums } from '../store/recentAlbumsStore';
import { useMaestroStore } from '../store/maestroStore';
import { getCachedLocalAlbumCoverMeta } from '../services/albumDetailCache';

const GOLD = '#C9A84C';
const INVALID_RECENT_TOOLTIP = 'File no longer available — re-add to library to play';

const GENRE_MAP = {
  jazz: { qobuzId: 80, label: 'Jazz' },
  classical: { qobuzId: 10, label: 'Classical' },
  rock: { qobuzId: 112, label: 'Rock' },
  blues: { qobuzId: 3, label: 'Blues' },
  folk: { qobuzId: 5, label: 'Folk' },
  alternative: { qobuzId: 112, label: 'Alternative' },
  pop: { qobuzId: 112, label: 'Pop' },
  rnb: { qobuzId: 127, label: 'R&B / Soul' },
  electronic: { qobuzId: 64, label: 'Electronic' },
  country: { qobuzId: 4, label: 'Country' },
  latin: { qobuzId: 149, label: 'Latin' },
  world: { qobuzId: 94, label: 'World' },
  ambient: { qobuzId: 64, label: 'Ambient' },
  acoustic: { qobuzId: 5, label: 'Acoustic' },
  progressive: { qobuzId: 112, label: 'Progressive' },
  avantgarde: { qobuzId: 80, label: 'Avant-garde' },
  hiphop: { qobuzId: 133, label: 'Hip-Hop' },
  metal: { qobuzId: 116, label: 'Metal' },
  soundtrack: { qobuzId: 91, label: 'Soundtracks' },
  soul: { qobuzId: 127, label: 'Soul / Funk' },
};

function getCover(a) {
  if (!a) return null;
  if (a.image?.large) return a.image.large;
  if (a.image?.small) return a.image.small;
  if (a.image?.thumbnail) return a.image.thumbnail;
  if (typeof a.image === 'string') return a.image;
  return a.cover || a.coverUrl || a.cover_url || a.coverUrlLarge || a.cover_url_large || a.artworkUrl || a.cover_art_url || null;
}

function stripQobuzPrefix(id) {
  return String(id || '').replace(/^qobuz:(album|track|artist):/, '');
}

function _looksLikeFilePath(s) {
  return typeof s === 'string' && (s.startsWith('/') || /^[A-Z]:\\/.test(s));
}

function mapAlbumForClick(raw) {
  const rawQobuzId = stripQobuzPrefix(raw.serviceAlbumId || raw.id);
  const isLocal = raw.__source === 'local_library' || raw.isLocalLibrary
    || raw.source === 'local_library' || !!raw.album_key
    || _looksLikeFilePath(raw.id) || _looksLikeFilePath(raw.serviceAlbumId);
  const src = isLocal ? 'local_library'
    : (('source' in raw && raw.source !== undefined && raw.source !== null) ? raw.source : 'qobuz');
  const mapped = normalizeAlbum({
    id: rawQobuzId,
    serviceAlbumId: rawQobuzId,
    title: raw.title || '',
    artist: raw.artist?.name || raw.artistName || '',
    artistName: raw.artist?.name || raw.artistName || '',
    artistId: raw.artist?.id ? String(raw.artist.id) : (raw.artistId ? stripQobuzPrefix(raw.artistId) : null),
    cover: getCover(raw),
    coverUrl: getCover(raw),
    hires: (getQualityTier(raw.maximum_bit_depth || raw.maxBitDepth, raw.maximum_sampling_rate || raw.maxSampleRate)?.tier ?? 'cd') !== 'cd',
    qualityTier: getQualityTier(raw.maximum_bit_depth || raw.maxBitDepth, raw.maximum_sampling_rate || raw.maxSampleRate),
    maxBitDepth: raw.maximum_bit_depth || raw.maxBitDepth || null,
    maxSampleRate: raw.maximum_sampling_rate || raw.maxSampleRate || null,
    releaseDate: raw.released_at || raw.release_date_original || raw.releaseDate || null,
    label: typeof raw.label === 'object' ? raw.label?.name : (raw.label || null),
    trackCount: raw.tracks_count || raw.trackCount || 0,
    genre: raw.genre,
    genres: raw.genres || (raw.genre ? [raw.genre] : []),
    source: src,
  }, src);
  if (raw.album_key) mapped.album_key = raw.album_key;
  if (raw.__source) mapped.__source = raw.__source;
  if (isLocal) {
    mapped.__source = 'local_library';
    mapped.isLocalLibrary = true;
    if (!mapped.album_key) mapped.album_key = raw.album_key || raw.id;
  } else if (raw.isLocalLibrary) {
    mapped.isLocalLibrary = true;
  }
  return mapped;
}

// ---------------------------------------------------------------------------
// Skeleton loader
// ---------------------------------------------------------------------------
function SkeletonCard({ wide }) {
  const w = wide ? 280 : CARD_SIZE;
  return (
    <div className="flex-shrink-0" style={{ width: w }}>
      <div className="rounded-xl" style={{ width: w, height: wide ? 140 : w, background: '#eee', animation: 'homeShimmer 1.5s ease-in-out infinite' }} />
      <div style={{ marginTop: 10 }}>
        <div style={{ width: '78%', height: 13, borderRadius: 6, background: '#eee', animation: 'homeShimmer 1.5s ease-in-out infinite' }} />
        <div style={{ width: '50%', height: 10, borderRadius: 5, background: '#f0f0f0', marginTop: 6, animation: 'homeShimmer 1.5s ease-in-out infinite' }} />
      </div>
    </div>
  );
}

function SkeletonRow({ count = 6, wide }) {
  return (
    <div style={{ display: 'flex', gap: 16, overflow: 'hidden', padding: '0 2px' }}>
      {Array.from({ length: count }, (_, i) => <SkeletonCard key={i} wide={wide} />)}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Data fetching helpers
// ---------------------------------------------------------------------------
async function fetchFeatured(type, limit = 20) {
  try {
    const r = await fetch(`${CLOUD_API_URL}/api/streaming/qobuz/featured?type=${type}&limit=${limit}`, { headers: getHeaders() });
    if (!r.ok) return [];
    const data = await r.json();
    return data?.albums?.items || [];
  } catch { return []; }
}

async function fetchGenreAlbums(genreId, limit = 8) {
  try {
    const r = await fetch(`${CLOUD_API_URL}/api/streaming/qobuz/genre-albums?genre_id=${genreId}&limit=${limit}`, { headers: getHeaders() });
    if (!r.ok) return [];
    const data = await r.json();
    return Array.isArray(data?.albums) ? data.albums : (data?.albums?.items || []);
  } catch { return []; }
}

async function fetchFeaturedPlaylists(limit = 10) {
  try {
    const r = await fetch(`${CLOUD_API_URL}/api/streaming/qobuz/playlists/featured?limit=${limit}`, { headers: getHeaders() });
    if (!r.ok) return [];
    const data = await r.json();
    return data?.playlists?.items || [];
  } catch { return []; }
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function getUserGenres() {
  try {
    const stored = localStorage.getItem('maestro_onboarding_genres');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {}

  try {
    const auth = getAuthState();
    if (auth?.onboardingGenres) return auth.onboardingGenres;
  } catch {}

  try {
    const profileStr = localStorage.getItem('maestro_personalization');
    if (profileStr) {
      const p = JSON.parse(profileStr);
      const g = p?.genres;
      if (Array.isArray(g) && g.length > 0) return g;
      if (g && typeof g === 'object') {
        const keys = Object.keys(g).filter(k => g[k] > 0);
        if (keys.length > 0) return keys;
      }
    }
  } catch {}

  return ['jazz', 'classical', 'rock'];
}

// ---------------------------------------------------------------------------
// Station card for home page
// ---------------------------------------------------------------------------
const HOME_STATION_GRADIENTS = [
  'linear-gradient(135deg, #1a1a2e, #16213e, #0f3460)',
  'linear-gradient(135deg, #2d1b69, #11998e)',
  'linear-gradient(135deg, #654ea3, #eaafc8)',
  'linear-gradient(135deg, #0f0c29, #302b63, #24243e)',
  'linear-gradient(135deg, #141e30, #243b55)',
  'linear-gradient(135deg, #1f1c2c, #928dab)',
  'linear-gradient(135deg, #373b44, #4286f4)',
  'linear-gradient(135deg, #0f2027, #203a43, #2c5364)',
];

function homeStationGradient(id) {
  let hash = 0;
  for (let i = 0; i < (id || '').length; i++) hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  return HOME_STATION_GRADIENTS[Math.abs(hash) % HOME_STATION_GRADIENTS.length];
}

function formatStationPlayed(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffH = Math.floor(diffMs / 3600000);
  if (diffH < 1) return 'Just now';
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function HomeStationCard({ station, onClick, onShare, animDelay = 0 }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="flex-shrink-0 text-left group touch-manipulation select-none [-webkit-tap-highlight-color:transparent]"
      style={{ width: 200, animation: `homeFadeIn 0.4s ease ${animDelay}ms both`, position: 'relative' }}
      onMouseEnter={() => { if (prefersFinePointerHover()) setHovered(true); }}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        onClick={() => onClick(station)}
        style={{ all: 'unset', cursor: 'pointer', display: 'block', width: '100%' }}
      >
        <div
          className="rounded-xl overflow-hidden"
          style={{
            width: 200, height: 200, position: 'relative',
            background: station.imageUrl ? undefined : homeStationGradient(station.id),
            boxShadow: hovered ? '0 12px 36px rgba(0,0,0,0.25)' : '0 4px 16px rgba(0,0,0,0.10)',
            border: '1px solid rgba(0,0,0,0.06)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            transform: hovered ? 'scale(1.03)' : 'scale(1)',
          }}
        >
          {station.imageUrl ? (
            <img src={station.imageUrl} alt={station.name} className="w-full h-full object-cover pointer-events-none" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ color: GOLD, fontSize: 36, opacity: 0.4 }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="8" width="2.5" height="8" rx="1.25" fill={GOLD} opacity="0.6" />
                <rect x="6.5" y="5" width="2.5" height="14" rx="1.25" fill={GOLD} opacity="0.8" />
                <rect x="11" y="3" width="2.5" height="18" rx="1.25" fill={GOLD} />
                <rect x="15.5" y="6" width="2.5" height="12" rx="1.25" fill={GOLD} opacity="0.8" />
                <rect x="20" y="9" width="2.5" height="6" rx="1.25" fill={GOLD} opacity="0.6" />
              </svg>
            </div>
          )}

          {/* Play button overlay on hover — no text */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: hovered ? 1 : 0,
            transition: 'opacity 0.2s ease', zIndex: 2,
            pointerEvents: 'none',
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: '50%',
              background: `linear-gradient(135deg, ${GOLD}, #d4a843)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 4px 16px ${GOLD}40`,
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#0a0a0f">
                <path d="M5 4l16 8-16 8V4z" />
              </svg>
            </div>
          </div>

          {/* Community badges */}
          {station.shared_to_community && (
            <div title="Shared with community" style={{ position: 'absolute', bottom: 8, left: 8, width: 22, height: 22, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
            </div>
          )}
          {station.imported_from_community && !station.shared_to_community && (
            <div title="From community" style={{ position: 'absolute', bottom: 8, left: 8, width: 22, height: 22, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
            </div>
          )}
        </div>

        <div style={{ marginTop: 10, paddingRight: 4 }}>
          <div style={{
            fontSize: 14, fontWeight: 700, color: 'var(--text-primary)',
            overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
          }}>
            {station.name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
            {station.lastPlayedAt && (
              <span style={{ fontSize: 11, color: '#86868b' }}>{formatStationPlayed(station.lastPlayedAt)}</span>
            )}
            {station.playCount > 0 && (
              <span style={{ fontSize: 11, color: '#86868b' }}>
                · {station.playCount} play{station.playCount !== 1 ? 's' : ''}
              </span>
            )}
            {!station.lastPlayedAt && !station.playCount && (
              <span style={{ fontSize: 11, color: '#86868b', fontStyle: 'italic' }}>New</span>
            )}
          </div>
        </div>
      </button>

      {/* Always-visible share icon */}
      {onShare && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onShare(station); }}
          title="Share station"
          style={{
            position: 'absolute', top: 8, right: 8, zIndex: 5,
            width: 30, height: 30, borderRadius: '50%',
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.8)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------
const FAV_SORT_OPTIONS = [
  { value: 'recent', label: 'Recently Added' },
  { value: 'az', label: 'A → Z' },
  { value: 'za', label: 'Z → A' },
  { value: 'artist', label: 'By Artist' },
];

function sortFavAlbums(albums, sortKey) {
  if (!albums || albums.length === 0) return albums;
  const sorted = [...albums];
  switch (sortKey) {
    case 'az':
      return sorted.sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }));
    case 'za':
      return sorted.sort((a, b) => (b.title || '').localeCompare(a.title || '', undefined, { sensitivity: 'base' }));
    case 'artist':
      return sorted.sort((a, b) => {
        const cmp = (a.artistName || '').localeCompare(b.artistName || '', undefined, { sensitivity: 'base' });
        if (cmp !== 0) return cmp;
        return (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
      });
    default:
      return sorted;
  }
}

function FavSortDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);

  const current = FAV_SORT_OPTIONS.find((o) => o.value === value) || FAV_SORT_OPTIONS[0];

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          background: 'none', border: '1px solid var(--border, rgba(255,255,255,0.12))',
          borderRadius: 8, padding: '4px 10px', cursor: 'pointer',
          fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
          display: 'flex', alignItems: 'center', gap: 4,
          transition: 'border-color 0.15s, color 0.15s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border, rgba(255,255,255,0.12))'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M3 6h18M3 12h12M3 18h6" />
        </svg>
        {current.label}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
          <polyline points={open ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 100,
          background: 'var(--bg-primary, #1a1a2e)', border: '1px solid var(--border, rgba(255,255,255,0.12))',
          borderRadius: 10, padding: 4, minWidth: 160,
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        }}>
          {FAV_SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '8px 12px', border: 'none', borderRadius: 6,
                background: opt.value === value ? 'rgba(201,168,76,0.12)' : 'transparent',
                color: opt.value === value ? GOLD : 'var(--text-primary)',
                fontSize: 13, fontWeight: opt.value === value ? 700 : 500,
                cursor: 'pointer', transition: 'background 0.12s',
              }}
              onMouseEnter={(e) => { if (opt.value !== value) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
              onMouseLeave={(e) => { if (opt.value !== value) e.currentTarget.style.background = 'transparent'; }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HomeView({
  user,
  counts,
  recentAlbums,
  recentArtists,
  playlists,
  onOpenAlbum,
  onHydrateAndOpenAlbum,
  onOpenArtist,
  onOpenPlaylist,
  onNavigateLibraryTab,
  library,
  onRemoveFavorite,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [removingFavIds, setRemovingFavIds] = useState(new Set());
  const [pickedForYou, setPickedForYou] = useState(null);
  const [newReleases, setNewReleases] = useState(null);
  const [editorPicks, setEditorPicks] = useState(null);
  const [featuredPlaylists, setFeaturedPlaylists] = useState(null);
  const [popularGenre, setPopularGenre] = useState(null);
  const [popularGenreName, setPopularGenreName] = useState('');
  const [loadingAlbumId, setLoadingAlbumId] = useState(null);
  const [myStations, setMyStations] = useState(null);
  const [favSort, setFavSort] = useState(() => {
    try { return localStorage.getItem('maestro_fav_sort') || 'recent'; } catch { return 'recent'; }
  });
  const { expandedSections, toggleSection } = useGridSections();
  const [shareTarget, setShareTarget] = useState(null);
  const [shareDescription, setShareDescription] = useState('');
  const [shareToast, setShareToast] = useState(null);
  const [ipcCovers, setIpcCovers] = useState({});
  const fetchedRef = useRef(false);

  const userGenres = useMemo(() => getUserGenres(), []);

  const homeContentReady = newReleases !== null && editorPicks !== null;
  useScrollRestore(location.pathname, {
    ready: homeContentReady,
    itemCount: homeContentReady ? 1 : 0,
  });

  const { hasCustomOrder, pinToTop, resetOrder, applySectionOrder } = useHomeSectionOrder();

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const genrePromises = userGenres.map(async (gKey) => {
      const info = GENRE_MAP[gKey];
      if (!info) return null;
      const albums = await fetchGenreAlbums(info.qobuzId, 30);
      return albums.length > 0 ? { genre: gKey, label: info.label, albums: shuffle(albums) } : null;
    });

    Promise.all(genrePromises).then((results) => {
      setPickedForYou(results.filter(Boolean));
    });

    fetchFeatured('new-releases', 30).then((a) => setNewReleases(shuffle(a)));
    fetchFeatured('editor-picks', 30).then((a) => setEditorPicks(shuffle(a)));
    fetchFeaturedPlaylists(12).then(setFeaturedPlaylists);

    maestroRadioService.getStations()
      .then(res => setMyStations(res.stations || []))
      .catch(() => setMyStations([]));

    const randomGenre = userGenres[Math.floor(Math.random() * userGenres.length)] || 'jazz';
    const gInfo = GENRE_MAP[randomGenre];
    if (gInfo) {
      setPopularGenreName(gInfo.label);
      fetchGenreAlbums(gInfo.qobuzId, 30).then((a) => setPopularGenre(shuffle(a)));
    }
  }, [userGenres]);

  const handleAlbumClick = useCallback(async (raw) => {
    if (loadingAlbumId) return;
    const mapped = mapAlbumForClick(raw);
    const aid = mapped.id || mapped.title;
    setLoadingAlbumId(aid);
    try {
      if (onHydrateAndOpenAlbum) {
        const ok = await onHydrateAndOpenAlbum(mapped);
        if (ok) return;
      }
      onOpenAlbum?.(mapped);
    } catch {
      onOpenAlbum?.(mapped);
    } finally {
      setLoadingAlbumId(null);
    }
  }, [loadingAlbumId, onHydrateAndOpenAlbum, onOpenAlbum]);

  const handleFavSort = useCallback((key) => {
    setFavSort(key);
    try { localStorage.setItem('maestro_fav_sort', key); } catch { /* ignore */ }
  }, []);

  const recentAlbumsArr = useMemo(
    () => filterRenderableRecentAlbums(Array.isArray(recentAlbums) ? recentAlbums : []),
    [recentAlbums],
  );
  const recentArtistsArr = useMemo(() => (Array.isArray(recentArtists) ? recentArtists : []), [recentArtists]);

  const hasLocalRecent = recentAlbumsArr.some((a) => a.source === 'local_library');
  const playbackLive = useMaestroStore(
    (s) => s.playbackState === 'playing' || s.playbackState === 'loading',
  );

  useEffect(() => {
    if (!isCoverIPCAvailable() || !hasLocalRecent || playbackLive) return;
    let cancelled = false;

    const hydrateCovers = async () => {
      const loaded = {};
      let droppedCovers = 0;
      const localRecents = recentAlbumsArr
        .filter((a) => a.source === 'local_library')
        .slice(0, 25);
      for (const a of localRecents) {
        const albumId = a.album_id;
        if (!albumId) continue;

        const storedEmb = normalizeCoverHash(a.embedded_cover_hash);
        const storedFol = normalizeCoverHash(a.folder_cover_hash);
        const cachedMeta = getCachedLocalAlbumCoverMeta(albumId);
        const emb = storedEmb || normalizeCoverHash(cachedMeta?.embedded_cover_hash);
        const fol = storedFol || normalizeCoverHash(cachedMeta?.folder_cover_hash);

        if (emb || fol) {
          const folderPath = cachedMeta?.library_folder_path || null;
          const url = await getCoverArtWithFallback(emb, fol, folderPath, { albumId });
          if (url) {
            loaded[albumId] = url;
            continue;
          }
        }

        const enrichedFallback = a.artwork_url
          && !isBadCoverUrl(a.artwork_url)
          && !isCoverProxyUrl(a.artwork_url)
          ? a.artwork_url
          : null;
        if (enrichedFallback) {
          loaded[albumId] = enrichedFallback;
        } else {
          droppedCovers += 1;
        }
      }
      if (!cancelled) {
        setIpcCovers(loaded);
        diagnosticLog('info', 'navigation', `[recents] rendered n=${localRecents.length} dropped_covers=${droppedCovers}`, {
          rendered: localRecents.length,
          dropped_covers: droppedCovers,
          resolved_covers: Object.keys(loaded).length,
        });
      }
    };

    let idleId = null;
    let timeoutId = null;
    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(() => { void hydrateCovers(); }, { timeout: 15_000 });
    } else {
      timeoutId = window.setTimeout(() => { void hydrateCovers(); }, 1200);
    }

    return () => {
      cancelled = true;
      if (idleId != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, [hasLocalRecent, recentAlbumsArr, playbackLive]);

  const favAlbums = useMemo(() => {
    const lib = Array.isArray(library) ? library : [];
    const filtered = lib.filter((a) => a?.title);
    return sortFavAlbums(filtered, favSort);
  }, [library, favSort]);

  const homeSections = useMemo(() => buildHomeSections({
    SkeletonRow,
    HomeStationCard,
    FavSortDropdown,
    navigate,
    setShareTarget,
    myStations,
    recentAlbumsArr,
    expandedSections,
    toggleSection,
    ipcCovers,
    favAlbums,
    favSort,
    handleFavSort,
    removingFavIds,
    setRemovingFavIds,
    onRemoveFavorite,
    pickedForYou,
    newReleases,
    editorPicks,
    featuredPlaylists,
    onOpenPlaylist,
    popularGenreName,
    popularGenre,
    handleAlbumClick,
  }), [
    navigate, myStations, recentAlbumsArr, expandedSections, toggleSection,
    ipcCovers,
    favAlbums, favSort, handleFavSort, removingFavIds, onRemoveFavorite,
    pickedForYou, newReleases, editorPicks, featuredPlaylists, onOpenPlaylist,
    popularGenreName, popularGenre, handleAlbumClick,
  ]);

  const displayName = useMemo(() => {
    const u = user || null;
    const n = String(
      u?.display_name ||
      u?.user_metadata?.display_name ||
      u?.user_metadata?.full_name ||
      u?.name || u?.full_name || u?.fullName || ''
    ).trim();
    if (n) return n.split(' ')[0];
    const e = String(u?.email || '').trim();
    if (e) return e.split('@')[0];
    return 'there';
  }, [user]);

  const confirmShareToCommunity = useCallback(async () => {
    if (!shareTarget) return;
    try {
      const desc = shareDescription.trim() || undefined;
      await sharingService.createShare(shareTarget.type, shareTarget.id, true, { description: desc });
      setShareToast(`"${shareTarget.name}" shared with the community!`);
      setTimeout(() => setShareToast(null), 3000);
    } catch (err) {
      console.error('[Share] Failed:', err);
      setShareToast('Failed to share. Please try again.');
      setTimeout(() => setShareToast(null), 3000);
    }
    setShareTarget(null);
    setShareDescription('');
  }, [shareTarget, shareDescription]);

  return (
    <div style={{ padding: '20px 24px 140px', color: 'var(--text-primary)', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>

      {/* Hero greeting */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, margin: 0, color: 'var(--text-primary)' }}>
          Hey, {displayName}
        </h1>
        <p style={{ fontSize: 15, color: 'var(--text-secondary)', marginTop: 6, margin: 0 }}>
          Here's what's fresh for you today.
        </p>
      </div>

      <HomeSectionList
        sections={homeSections}
        applySectionOrder={applySectionOrder}
        pinToTop={pinToTop}
        resetOrder={resetOrder}
        hasCustomOrder={hasCustomOrder}
      />

      {/* Share-to-community confirmation modal */}
      {shareTarget && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} onClick={() => setShareTarget(null)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 420, width: '90%', border: '1px solid #e0e0e0', boxShadow: '0 8px 40px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              {shareTarget.artwork ? (
                <img src={shareTarget.artwork} alt="" style={{ width: 60, height: 60, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
              ) : (
                <div style={{ width: 60, height: 60, borderRadius: 10, background: '#faf5e8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2"><circle cx="12" cy="12" r="2"/><path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14"/></svg>
                </div>
              )}
              <div style={{ minWidth: 0 }}>
                <h3 style={{ color: '#1a1a1a', fontSize: 18, fontWeight: 700, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Share &ldquo;{shareTarget.name}&rdquo;</h3>
                <span style={{ fontSize: 12, color: '#888', textTransform: 'capitalize' }}>{shareTarget.type}</span>
              </div>
            </div>
            <p style={{ color: '#555', fontSize: 14, margin: '0 0 16px', lineHeight: 1.5 }}>
              This will add your {shareTarget.type} to the Community page where all Maestro members can discover it, preview the tracks, and add it to their own collection.
            </p>
            <div style={{ marginBottom: 16 }}>
              <label style={{ color: '#444', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Why are you sharing this? (optional)</label>
              <textarea
                value={shareDescription}
                onChange={(e) => setShareDescription(e.target.value)}
                placeholder="e.g., 'Perfect for late night listening'"
                maxLength={300}
                rows={3}
                style={{ width: '100%', background: '#fff', border: '1px solid #ccc', borderRadius: 8, padding: '10px 12px', color: '#333', fontSize: 13, resize: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
              />
              <div style={{ textAlign: 'right', fontSize: 10, color: '#999', marginTop: 4 }}>{shareDescription.length}/300</div>
            </div>
            {displayName && displayName !== 'there' ? (
              <p style={{ color: '#555', fontSize: 13, margin: '0 0 18px' }}>
                Will appear as: Shared by <strong style={{ color: '#b8860b' }}>{displayName}</strong>
              </p>
            ) : (
              <p style={{ color: '#666', fontSize: 12, margin: '0 0 18px', background: '#faf8f2', padding: '10px 12px', borderRadius: 8, border: '1px solid #f0e8d0' }}>
                Set your public name in Settings → Account so members know who shared this.
              </p>
            )}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShareTarget(null); setShareDescription(''); }} style={{ padding: '10px 20px', background: '#fff', color: '#333', border: '1px solid #ddd', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button onClick={confirmShareToCommunity} style={{ padding: '10px 20px', background: GOLD, color: '#1a1a2e', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Share with Community</button>
            </div>
          </div>
        </div>
      )}

      {/* Share toast */}
      {shareToast && (
        <div style={{ position: 'fixed', bottom: 100, left: '50%', transform: 'translateX(-50%)', zIndex: 50001, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', color: '#fff', padding: '10px 20px', borderRadius: 10, fontSize: 13, fontWeight: 600, boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
          {shareToast}
        </div>
      )}

      <style>{`
        @keyframes homeShimmer { 0% { opacity: 0.5; } 50% { opacity: 1; } 100% { opacity: 0.5; } }
        @keyframes homeFadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
